import { convexQuery } from "@convex-dev/react-query";
import { formatDateUTC } from "@plasma/utils/time";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  type CashDrawerSummary as CashDrawerBase,
  cashDrawerGetTransactionsByLocation,
} from "~/api-refs";
import { toConvexTableId } from "~/lib/convex-table-id";

import { SalesDataTable } from "../sales-data-table";

type CashDrawerSummary = CashDrawerBase & {
  startOfDayDate?: Date;
  startOfDayAmount?: number;
  cashSales?: number;
  totalDepositsWithdrawls?: number;
  expectedEndOfDayAmount?: number;
  endOfDayAmount?: number;
};

interface DrawerBalancesViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const DrawerBalancesView = ({
  locationId,
  dateRange,
  tableId,
}: DrawerBalancesViewProps) => {
  const { data, isPending: isLoading } = useQuery(
    convexQuery(cashDrawerGetTransactionsByLocation, {
      startDate: dateRange.from.getTime(),
      endDate: dateRange.to.getTime(),
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const cashDrawerData = (data ?? []) as CashDrawerSummary[];

  const columnHelper = createColumnHelper<CashDrawerSummary>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("session", {
          header: "POS Device",
          cell: ({ getValue }) => {
            const session = getValue();
            return session
              ? `${session.deviceInfo.model} (${session.deviceInfo.os})`
              : "N/A";
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.session
              ? filterValue.includes(original.session.deviceId)
              : false,
          meta: {
            faceting: {
              title: "POS Device",
              getOption: (r: CashDrawerSummary["session"]) => ({
                value: r?.deviceId ?? "",
                label: r ? `${r.deviceInfo.model} (${r.deviceInfo.os})` : "",
              }),
            },
            getCellForExport: (r: CashDrawerSummary["session"]) =>
              r ? `${r.deviceInfo.model} (${r.deviceInfo.os})` : "",
          },
        }),
        columnHelper.accessor("startOfDayDate", {
          id: "startOfDayDate - date",
          header: " Date",
          cell: ({ getValue }) => {
            const date = getValue();
            if (!date) {
              return "N/A";
            }
            return formatDateUTC(date, "M/D/YYYY");
          },
          meta: {
            getCellForExport: (d: CashDrawerSummary["startOfDayDate"]) =>
              d?.toISOString() ?? "",
          },
        }),
        columnHelper.accessor("startOfDayDate", {
          id: "startOfDayDate - time",
          header: "Time",
          cell: ({ getValue }) => {
            const date = getValue();
            if (!date) {
              return "N/A";
            }
            return formatDateUTC(date, "HH:mm:ss");
          },
          meta: {
            getCellForExport: (d: CashDrawerSummary["startOfDayDate"]) =>
              d?.toISOString() ?? "",
          },
        }),
        columnHelper.accessor("startOfDayAmount", {
          header: "Start of Day",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: CashDrawerSummary["startOfDayAmount"]) =>
              d ?? null,
          },
        }),
        columnHelper.accessor("cashSales", {
          header: "Cash Sales",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: CashDrawerSummary["cashSales"]) => d ?? null,
          },
        }),
        columnHelper.accessor("totalDepositsWithdrawls", {
          header: "Total Deposits/Withdrawals",
          cell: ({ getValue }) => {
            const value = getValue();
            if (!value || value === 0) {
              return "$0.00";
            }
            const sign = value > 0 ? "(+)" : "(-)";
            return `${sign} ${Math.abs(value).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`;
          },
          meta: {
            getCellForExport: (
              d: CashDrawerSummary["totalDepositsWithdrawls"]
            ) => d ?? 0,
          },
        }),
        columnHelper.accessor("expectedEndOfDayAmount", {
          header: "Expected EOD",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (
              d: CashDrawerSummary["expectedEndOfDayAmount"]
            ) => d ?? null,
          },
        }),
        columnHelper.accessor("endOfDayAmount", {
          header: "End of Day Total",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: CashDrawerSummary["endOfDayAmount"]) =>
              d ?? null,
          },
        }),
        columnHelper.accessor("employee", {
          header: "Employee EOD",
          cell: ({ getValue }) => {
            const employee = getValue();
            if (!employee) {
              return "N/A";
            }
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.employee
              ? filterValue.includes(original.employee._id)
              : false,
          meta: {
            getCellForExport: (e: CashDrawerSummary["employee"]) =>
              e ? `${e.firstName} ${e.lastName}` : "N/A",
            faceting: {
              title: "Employee",
              getOption: (e: CashDrawerSummary["employee"]) => ({
                value: e?._id ?? "",
                label: e ? `${e.firstName} ${e.lastName}` : "N/A",
              }),
            },
          },
        }),
      ] as ColumnDef<CashDrawerSummary>[],
    [columnHelper]
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="border-foreground mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2" />
          <p className="text-foreground">Loading cash drawer data...</p>
        </div>
      </div>
    );
  }

  return (
    <SalesDataTable columns={columns} data={cashDrawerData} id={tableId} />
  );
};
